/**
 * UTM persistence helper.
 * Captures utm_source/utm_medium/utm_campaign/utm_term/utm_content/gclid/yclid
 * from the URL and stores in localStorage for 30 days.
 * Use getStoredUTM() at signup/payment time to enrich attribution.
 */

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "yclid",
] as const;

const STORAGE_KEY = "eridon_utm";
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

export type UTMRecord = Partial<Record<(typeof UTM_KEYS)[number], string>> & {
  capturedAt?: number;
  landingPath?: string;
  referrer?: string;
};

export function captureUTM(): void {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    const captured: UTMRecord = {};
    for (const k of UTM_KEYS) {
      const v = params.get(k);
      if (v) (captured as any)[k] = v;
    }
    if (Object.keys(captured).length === 0) return;
    captured.capturedAt = Date.now();
    captured.landingPath = window.location.pathname;
    captured.referrer = document.referrer || undefined;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(captured));
  } catch {}
}

export function getStoredUTM(): UTMRecord | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as UTMRecord;
    if (!parsed.capturedAt || Date.now() - parsed.capturedAt > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearStoredUTM(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
}
